import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([])
  const [filteredSuppliers, setFilteredSuppliers] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ search: '' })

  const fetchSuppliers = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get(API_ENDPOINTS.SUPPLIERS)
      setSuppliers(response.data)
      setFilteredSuppliers(response.data)
    } catch (error) {
      console.error('Error fetching suppliers:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Failed to fetch suppliers. Please try again.'
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSuppliers()
  }, [])

  useEffect(() => {
    let filtered = [...suppliers]
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(supplier =>
        supplier.name?.toLowerCase().includes(searchLower) ||
        supplier.email?.toLowerCase().includes(searchLower) ||
        supplier.phone?.toLowerCase().includes(searchLower)
      )
    }
    setFilteredSuppliers(filtered)
  }, [suppliers, filters])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleRowClick = (supplier, e) => {
    if (e.target.closest('button') || e.target.closest('.btn')) {
      return
    }

    const htmlContent = `
      <div style="text-align: left; font-size: 14px;">
        <p><strong>ID:</strong> ${supplier.id}</p>
        <p><strong>Name:</strong> ${supplier.name}</p>
        <p><strong>Email:</strong> ${supplier.email || 'N/A'}</p>
        <p><strong>Phone:</strong> ${supplier.phone || 'N/A'}</p>
        <p><strong>Address:</strong> ${supplier.address || 'N/A'}</p>
        <p><strong>Created:</strong> ${formatDate(supplier.created_at)}</p>
      </div>
    `

    Swal.fire({
      title: 'Supplier Details',
      html: htmlContent,
      icon: 'info',
      width: '500px',
      confirmButtonText: 'Close',
      confirmButtonColor: '#3085d6'
    })
  }

  const escapeHtml = (text) => {
    if (!text) return ''
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }
    return text.replace(/[&<>"']/g, m => map[m])
  }

  const handleEdit = async (supplier) => {
    const escapedName = escapeHtml(supplier.name || '')
    const escapedEmail = escapeHtml(supplier.email || '')
    const escapedPhone = escapeHtml(supplier.phone || '')
    const escapedAddress = escapeHtml(supplier.address || '')

    const { value: formValues } = await Swal.fire({
      title: '<span style="color: #333; font-size: 24px;">✏️ Edit Supplier</span>',
      html: `
        <div style="text-align: left; padding: 10px 0;">
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Supplier Name <span style="color: red;">*</span></label>
          <input id="swal-name" class="swal2-input" value="${escapedName}" placeholder="Enter supplier name" style="margin-top: 0;">
          
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Email</label>
          <input id="swal-email" class="swal2-input" type="email" value="${escapedEmail}" placeholder="Enter email (Optional)" style="margin-top: 0;">
          
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Phone</label>
          <input id="swal-phone" class="swal2-input" value="${escapedPhone}" placeholder="Enter phone (Optional)" style="margin-top: 0;">
          
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Address</label>
          <textarea id="swal-address" class="swal2-textarea" placeholder="Enter address (Optional)" style="margin-top: 0; min-height: 80px;">${escapedAddress}</textarea>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: '<i class="bi bi-check-circle"></i> Update Supplier',
      confirmButtonColor: '#3085d6',
      cancelButtonText: '<i class="bi bi-x-circle"></i> Cancel',
      cancelButtonColor: '#6c757d',
      width: '550px',
      customClass: {
        popup: 'swal2-popup-custom'
      },
      preConfirm: () => {
        const name = document.getElementById('swal-name').value.trim()
        const email = document.getElementById('swal-email').value.trim()
        const phone = document.getElementById('swal-phone').value.trim()
        const address = document.getElementById('swal-address').value.trim()

        if (!name) {
          Swal.showValidationMessage('Supplier name is required')
          return false
        }

        return {
          name,
          email: email || null,
          phone: phone || null,
          address: address || null
        }
      }
    })

    if (!formValues) return

    try {
      const payload = { ...formValues }
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === '') {
          delete payload[key]
        }
      })

      await apiClient.put(`${API_ENDPOINTS.SUPPLIERS}${supplier.id}/`, payload)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Supplier updated successfully!'
      })
      fetchSuppliers()
    } catch (error) {
      console.error('Error updating supplier:', error)
      const errorMessage = error.response?.data?.name?.[0] ||
        error.response?.data?.non_field_errors?.[0] ||
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to update supplier. Please try again.'

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      })
    }
  }

  const handleDelete = async (supplierId) => {
    const supplier = suppliers.find(sup => sup.id === supplierId)
    const escapedName = escapeHtml(supplier?.name || '')

    const { value: typedName } = await Swal.fire({
      title: 'Delete Supplier',
      html: `
        <p>Are you sure you want to delete this supplier?</p>
        <p class="text-danger"><strong>This action cannot be undone!</strong></p>
        <p>Type <strong>"${escapedName}"</strong> to confirm:</p>
        <input id="swal-input" class="swal2-input" placeholder="Type supplier name">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
      allowEscapeKey: true,
      didOpen: () => {
        const input = document.getElementById('swal-input')
        const confirmButton = Swal.getConfirmButton()

        confirmButton.disabled = true
        confirmButton.style.opacity = '0.5'
        confirmButton.style.cursor = 'not-allowed'

        input.addEventListener('input', () => {
          const inputValue = input.value.trim()
          const matches = inputValue === supplier?.name

          if (matches) {
            confirmButton.disabled = false
            confirmButton.style.opacity = '1'
            confirmButton.style.cursor = 'pointer'
          } else {
            confirmButton.disabled = true
            confirmButton.style.opacity = '0.5'
            confirmButton.style.cursor = 'not-allowed'
          }
        })

        input.focus()
      },
      preConfirm: () => {
        const input = document.getElementById('swal-input')
        const inputValue = input.value.trim()

        if (!inputValue) {
          Swal.showValidationMessage('You need to type the supplier name!')
          return false
        }

        if (inputValue !== supplier?.name) {
          Swal.showValidationMessage('The name does not match!')
          return false
        }

        return inputValue
      }
    })

    if (typedName !== supplier?.name) return

    try {
      await apiClient.delete(`${API_ENDPOINTS.SUPPLIERS}${supplierId}/`)
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Supplier has been deleted.',
        timer: 2000,
        showConfirmButton: false
      })
      fetchSuppliers()
    } catch (error) {
      console.error('Error deleting supplier:', error)
      const errorMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to delete supplier. Please try again.'

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      })
    }
  }

  const totalSuppliers = filteredSuppliers.length

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Suppliers</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Suppliers</li>
            <li className="breadcrumb-item active">Manage Suppliers</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{
                  paddingBottom: '15px',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  <div className="d-flex align-items-center gap-4 flex-wrap">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-truck me-2"></i>
                      Manage Suppliers
                    </h5>

                    <div className="d-flex align-items-center" style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: '600',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                    }}>
                      <i className="bi bi-truck me-2"></i>
                      <span>Total: <strong>{totalSuppliers}</strong></span>
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search suppliers..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                </div>

                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Address</th>
                          <th className="text-end" style={{ width: '150px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSuppliers.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center">No suppliers found</td>
                          </tr>
                        ) : (
                          filteredSuppliers.map(supplier => (
                            <tr
                              key={supplier.id}
                              onClick={(e) => handleRowClick(supplier, e)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td className="align-middle">
                                <strong>{supplier.name}</strong>
                              </td>
                              <td className="align-middle">{supplier.email || 'N/A'}</td>
                              <td className="align-middle">{supplier.phone || 'N/A'}</td>
                              <td className="align-middle">{supplier.address ? (supplier.address.length > 50 ? supplier.address.substring(0, 50) + '...' : supplier.address) : 'N/A'}</td>
                              <td className="align-middle text-end" onClick={(e) => e.stopPropagation()}>
                                <div className="btn-group" role="group">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleEdit(supplier)}
                                    title="Edit supplier"
                                  >
                                    <i className="bi bi-pencil-fill"></i>
                                    <span className="ms-1 d-none d-md-inline">Edit</span>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDelete(supplier.id)}
                                    title="Delete supplier"
                                  >
                                    <i className="bi bi-trash-fill"></i>
                                    <span className="ms-1 d-none d-md-inline">Delete</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Suppliers

